import {
  $isListItemNode,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
} from '@lexical/list';
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  UNORDERED_LIST,
} from '@lexical/markdown';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { Icons } from '@shared/components/content/icons';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_TAB_COMMAND,
} from 'lexical';
import { useEffect, useRef, useState } from 'react';

const MD_TRANSFORMERS = [
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  UNORDERED_LIST,
];

const LEXICAL_NODES = [ListNode, ListItemNode];

const LEXICAL_THEME = {
  list: { ul: 'ml-4 list-disc', listitem: 'my-0.5' },
  text: { bold: 'font-semibold', italic: 'italic' },
};

function Toolbar() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isList, setIsList] = useState(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));
        const anchorNode = selection.anchor.getNode();
        setIsList($isListItemNode(anchorNode.getParent()));
      });
    });
  }, [editor]);

  return (
    <div className="bg-muted/50 flex items-center gap-0.5 border-b px-2 py-1.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn('size-7', isBold && 'bg-muted')}
        title="Bold"
        icon={<Icons.bold />}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn('size-7', isItalic && 'bg-muted')}
        title="Italic"
        icon={<Icons.italic />}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn('size-7', isList && 'bg-muted')}
        title="Bullet list"
        icon={<Icons.list />}
        onClick={() =>
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        }
      />
    </div>
  );
}

function ListKeyboardPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    const unregisterTab = editor.registerCommand(
      KEY_TAB_COMMAND,
      (event: KeyboardEvent) => {
        event.preventDefault();
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    const unregisterBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event: KeyboardEvent) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed())
          return false;
        if (selection.anchor.offset !== 0) return false;

        const anchorNode = selection.anchor.getNode();
        const listItem = $isListItemNode(anchorNode)
          ? anchorNode
          : anchorNode.getParent();
        if (!$isListItemNode(listItem)) return false;

        // Only at the very start of the list item (empty item or first child at offset 0)
        if (anchorNode !== listItem && anchorNode !== listItem.getFirstChild())
          return false;

        event.preventDefault();
        const paragraph = $createParagraphNode();
        listItem.getChildren().forEach((child) => paragraph.append(child));
        listItem.replace(paragraph);
        paragraph.selectStart();
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      unregisterTab();
      unregisterBackspace();
    };
  }, [editor]);
  return null;
}

function SyncPlugin({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const editorMarkdownRef = useRef(value);
  const isInternalChangeRef = useRef(false);

  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    if (value.trim() !== editorMarkdownRef.current.trim()) {
      editorMarkdownRef.current = value;
      editor.update(() => {
        $convertFromMarkdownString(value, MD_TRANSFORMERS);
      });
    }
  }, [editor, value]);

  return (
    <OnChangePlugin
      onChange={(editorState) => {
        editorState.read(() => {
          const markdown = $convertToMarkdownString(MD_TRANSFORMERS);
          editorMarkdownRef.current = markdown;
          isInternalChangeRef.current = true;
          onChange(markdown);
        });
      }}
    />
  );
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  namespace?: string;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start typing...',
  namespace = 'rich-text-editor',
}: RichTextEditorProps) {
  const initialConfig = {
    namespace,
    nodes: LEXICAL_NODES,
    theme: LEXICAL_THEME,
    onError: (error: Error) => console.error(error),
    editorState: () => $convertFromMarkdownString(value, MD_TRANSFORMERS),
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="overflow-hidden rounded-md border shadow-sm">
        <Toolbar />
        <div className="relative bg-background">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="min-h-[160px] px-4 py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50" />
            }
            placeholder={
              <div className="pointer-events-none absolute left-4 top-3 text-sm text-gray-300">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <ListPlugin />
        <HistoryPlugin />
        <MarkdownShortcutPlugin transformers={MD_TRANSFORMERS} />
        <ListKeyboardPlugin />
        <SyncPlugin value={value} onChange={onChange} />
      </div>
    </LexicalComposer>
  );
}
